/**
 * AuditTimeline — renders audit entries as a true timeline with a vertical
 * rail, per-user avatar nodes, day separators, and relative timestamps.
 *
 * Click a row to expand before/after state. Phase 13 Polish-Pass #2.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { formatDateTime, formatRelativeTime } from '@armard/shared';
import {
  auditActionLabel,
  auditEntityLabel,
  auditUserLabel,
  useAuditEntry,
  type AuditListEntry,
} from '../../hooks/useAuditLog';
import { LoadingSpinner } from '../ui';
import type { AuditAction } from '@armard/shared';
import { AuditAvatar } from './AuditAvatar';
import { AuditDaySeparator } from './AuditDaySeparator';
import { AuditDiff } from './AuditDiff';
import { groupByDay, useLiveTick } from './auditGrouping';

interface AuditTimelineProps {
  entries: AuditListEntry[];
  showEntityLink?: boolean;
  emptyMessage?: string;
}

const ACTION_VERB: Record<string, string> = {
  create: 'erstellt',
  update: 'geändert',
  delete: 'gelöscht',
  merge: 'zusammengeführt',
};

const ACTION_TEXT: Record<AuditAction, string> = {
  create: 'text-green-300',
  update: 'text-blue-300',
  delete: 'text-red-300',
};

export function AuditTimeline({
  entries,
  showEntityLink = false,
  emptyMessage = 'Keine Einträge vorhanden.',
}: AuditTimelineProps) {
  // Tick every 30s so relative timestamps stay live.
  useLiveTick(30_000);

  if (entries.length === 0) {
    return <p className="text-center py-6 text-white/40 text-sm">{emptyMessage}</p>;
  }

  const groups = groupByDay(entries);

  return (
    <div>
      {groups.map((group) => (
        <section key={group.dayKey}>
          <AuditDaySeparator label={group.label} />
          <ul>
            {group.entries.map((entry) => (
              <AuditTimelineRow
                key={entry.id}
                entry={entry}
                showEntityLink={showEntityLink}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

interface AuditTimelineRowProps {
  entry: AuditListEntry;
  showEntityLink: boolean;
}

function AuditTimelineRow({ entry, showEntityLink }: AuditTimelineRowProps) {
  const [expanded, setExpanded] = useState(false);
  const action = entry.action as AuditAction;
  const verb = ACTION_VERB[entry.action] ?? auditActionLabel(entry.action).toLowerCase();
  const verbClass = ACTION_TEXT[action] ?? 'text-white/70';

  return (
    <li
      className="
        relative
        before:content-[''] before:absolute before:left-3 before:w-px before:bg-white/6
        before:top-0 before:bottom-0
        first:before:top-[22px]
        last:before:bottom-[calc(100%_-_22px)]
      "
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="group w-full text-left relative pl-10 pr-3 py-2.5 rounded-md hover:bg-white/3 transition-colors"
      >
        <span className="absolute left-0 top-2.5 z-10">
          <AuditAvatar
            userName={entry.userName}
            userEmail={entry.userEmail}
            userId={entry.userId}
            action={action}
          />
        </span>

        <div className="flex items-start gap-2 flex-wrap text-sm">
          <span className="text-white/80">
            {/* A10: natürliche Satzstellung — „X hat Rechnung … geändert" */}
            <span className="font-medium">{auditUserLabel(entry)}</span>
            <span className="text-white/50"> hat </span>
            {showEntityLink && (
              <>
                <span className="text-white/80">{auditEntityLabel(entry.entityType)}</span>
                <span className="mx-1">
                  <EntityLink entityType={entry.entityType} entityId={entry.entityId} />
                </span>
              </>
            )}
            <span className={`font-medium ${verbClass}`}>{verb}</span>
          </span>
          <span
            className="ml-auto text-xs text-white/40 shrink-0"
            title={formatDateTime(entry.timestamp)}
          >
            {formatRelativeTime(entry.timestamp)}
          </span>
        </div>

        <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
          {entry.changedFields && entry.changedFields.length > 0 ? (
            <span className="text-white/40">
              {entry.changedFields.length} Feld
              {entry.changedFields.length !== 1 ? 'er' : ''}:{' '}
              <span className="text-white/60">
                {entry.changedFields.slice(0, 4).join(', ')}
                {entry.changedFields.length > 4 ? ' …' : ''}
              </span>
            </span>
          ) : (
            <span className="text-white/30">{auditActionLabel(entry.action)}</span>
          )}
          <motion.span
            animate={{ rotate: expanded ? 90 : 0 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
            className="ml-auto text-white/30 group-hover:text-white/60 transition-colors"
            aria-hidden
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M3 2 L7 5 L3 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.span>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="detail"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.16, ease: 'easeOut' }}
          >
            <AuditDetail entryId={entry.id} action={action} />
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

// A10: Detail-Routen für alle verlinkbaren Entitäten — vorher nur
// invoice/customer, alle anderen UUIDs waren toter Text.
const ENTITY_ROUTES: Record<string, string> = {
  invoice: '/invoices/$id',
  customer: '/customers/$id',
  expense: '/expenses/$id',
  supplier: '/suppliers/$id',
  contract: '/contracts/$id',
  cost_estimate: '/estimates/$id',
};

function EntityLink({ entityType, entityId }: { entityType: string; entityId: string }) {
  const route = ENTITY_ROUTES[entityType];
  if (route) {
    return (
      <Link
        to={route}
        params={{ id: entityId }}
        className="text-xs text-white/50 hover:text-white transition-colors font-mono"
        onClick={(e) => e.stopPropagation()}
      >
        {entityId.slice(0, 8)}…
      </Link>
    );
  }
  return (
    <span className="text-xs text-white/40 font-mono" title={entityId}>
      {entityId.slice(0, 8)}…
    </span>
  );
}

function AuditDetail({ entryId, action }: { entryId: string; action: AuditAction }) {
  const { data, isLoading, error } = useAuditEntry(entryId);

  if (isLoading) {
    return (
      <div className="ml-10 mr-3 mt-1 mb-2 border border-white/6 rounded-md px-4 py-4 flex justify-center bg-white/[0.02]">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="ml-10 mr-3 mt-1 mb-2 border border-white/6 rounded-md px-4 py-3 text-sm text-red-400 bg-white/[0.02]">
        Details konnten nicht geladen werden.
      </div>
    );
  }

  const hasAny =
    (data.beforeState && Object.keys(data.beforeState).length > 0) ||
    (data.afterState && Object.keys(data.afterState).length > 0);

  return (
    <div className="ml-10 mr-3 mt-1 mb-2 border border-white/6 rounded-md px-4 py-3 bg-white/[0.02]">
      {hasAny ? (
        <AuditDiff
          before={data.beforeState}
          after={data.afterState}
          changedFields={data.changedFields}
          action={action}
        />
      ) : (
        <p className="text-xs text-white/40">Keine State-Snapshots gespeichert.</p>
      )}
    </div>
  );
}

